/** @module Auth0FeatureFlagHelper */
/* eslint-disable import/extensions */
// A small wrapper over LD client to support auto load (accounting for a consuming app already having a client avaiable)
import Auth0DynamicScriptLoader from './auth0-dynamic-script-loader.mjs';
import Auth0CdnResourceResolver from '../cdn/utils/auth0-cdn-resource-resolver.mst.mjs';
import logger from './logging/auth0-logger.mjs';

const externalScriptDependencies = [
  // TODO: maybe host this on SS CDN...
  { url: Auth0CdnResourceResolver.AUTH0_SCRIPT_URL_LAUNCHDARKLY, required: false, attributes: { crossorigin: 'anonymous' } },
];

class Auth0FeatureFlagHelper {
  constructor(options) {
    this._auth0TenantName = options.auth0Tenant;
    this._ldClientSideId = options.clientSideId;
    this._ldClient = options.ldClient; // optional, if app already has one handy
  }

  initialize = (context) => {
    console.log(`Auth0FeatureFlagHelper.initialize client => ${this._ldClientSideId}`);
    this._ldContext = {
      kind: 'user',
      anonymous: true,
      ...context,
      auth0Tenant: this._auth0TenantName,
    };
    console.log(`Auth0FeatureFlagHelper.initialize with context => ${JSON.stringify(this._ldContext)}`);

    // this can never fail (throw) into the app. If it fails locally, we just init an "empty" ff provider
    // so that it is capable to resolving all flag requests to their default values. As such, we always call
    // "resolve" on the promise (exception to this rule is for bad config)
    window.LDClientInitPromise = new Promise(async (resolve, reject) => {
      if (this._ldClient) {
        console.log('Auth0FeatureFlagHelper.initialize => already initialized');
        resolve(this); // already initialized
        return;
      }

      if (!this._ldClientSideId) {
        reject(new Error('The Auth0FeatureFlagHelper constructor must receive a valid `clientSideId` prop'));
        return;
      }

      if (window.LDClientInitPromise) {
        console.log('await global LDClientInitPromise...');
        await window.LDClientInitPromise;
      }

      if (!window.LDClient) {
        console.log(`Auth0FeatureFlagHelper.initialize => LDClient not present, loading... (${this._ldClientSideId})`);
        const loader = new Auth0DynamicScriptLoader(this._auth0TenantName);
        await loader.loadScripts(externalScriptDependencies);
        if (!window.LDClient) {
          logger.warn(`Auth0FeatureFlagHelper.initialize => unable to load LDClient from ${externalScriptDependencies[0].url}`);
          resolve(this);
          return;
        }
      }

      const client = window.LDClient.initialize(this._ldClientSideId, this._ldContext);
      client.on('ready', () => {
        console.log(`Auth0FeatureFlagHelper.initialize ${this._ldClientSideId} reports READY`);
        this._ldClient = client;
        this._initPromise = null;
        resolve(this);
      });
      client.on('failed', () => {
        logger.warn(`Auth0FeatureFlagHelper.initialize ${this._ldClientSideId} reports FAILED`);
        resolve(this);
      });
    });

    this._initPromise = window.LDClientInitPromise;
    return this._initPromise;
  };

  eval = async (flag, defaultValue) => {
    try {
      console.log(`Auth0FeatureFlagHelper.eval(${flag}, ${defaultValue}) starts...`);

      if (!this._ldClient && !this._initPromise) {
        logger.error('LaunchDarkly is not initialized. Call initialize prior to any flag eval...');
        return defaultValue;
      }
      const maybeInitPromise = this._initPromise ?? Promise.resolve();
      await maybeInitPromise;

      const val = this._ldClient.variation(flag, defaultValue);
      console.log(`Auth0FeatureFlagHelper.eval resolved variation for "${flag}" => "${val}"`);
      return val;
    } catch (error) {
      // do not use logger here!
      console.error(`Auth0FeatureFlagHelper.eval failed: ${error.message}`);
      return defaultValue;
    }
  };

  evalFlags = async (flags) => {
    try {
      console.log(`Auth0FeatureFlagHelper.evalFlags(${JSON.stringify(flags)} starts...`);

      if (!this._ldClient && !this._initPromise) {
        logger.error('LaunchDarkly is not initialized. Call initialize prior to any flag eval...');
        return flags.map(flag => flag.defaultValue);
      }
      const maybeInitPromise = this._initPromise ?? Promise.resolve();
      await maybeInitPromise;

      const ldPromises = flags.map(flag => this._ldClient.variation(flag.key, flag.defaultValue));
      const results = await Promise.all(ldPromises);
      console.log(`Auth0FeatureFlagHelper.evalFlags resolved variations => ${JSON.stringify(results)}`);

      const associativeObj = {};
      flags.forEach((flag, idx) => {
        associativeObj[flag.key] = results[idx];
      });
      return associativeObj;
    } catch (error) {
      // do not use logger here!
      console.error(`Auth0FeatureFlagHelper.evalFlags failed: ${error.message}`);
      return flags.map(flag => flag.defaultValue);
    }
  };

  get isInitialized() {
    return !!this._ldClient;
  }
}

export default Auth0FeatureFlagHelper;
